import {
  actionResponseSchema,
  notificationCenterResponseSchema,
  type Notification,
  type SessionIdentity,
} from "@cosmo/contracts";
import { useCallback, useEffect, useState } from "react";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

async function responseMessage(response: Response): Promise<string> {
  try {
    const body = await response.json() as { error?: { message?: string } };
    return body.error?.message || "No fue posible consultar los avisos.";
  } catch {
    return "No fue posible consultar los avisos.";
  }
}

export function NotificationCenter({ session, onOpenCancellations }: {
  session: SessionIdentity;
  onOpenCancellations: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [items, setItems] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!session.branchId) return;
    setLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/notifications`, {
        credentials: "include", headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      const result = notificationCenterResponseSchema.parse(await response.json()).data;
      setItems(result.notifications);
      setUnreadCount(result.unreadCount);
      setMessage("");
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "No fue posible consultar los avisos.");
    } finally {
      setLoading(false);
    }
  }, [session.branchId]);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 60_000);
    return () => window.clearInterval(timer);
  }, [refresh]);

  async function markRead(item: Notification) {
    const response = await fetch(`${apiBaseUrl}/api/v1/notifications/read`, {
      method: "POST", credentials: "include",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ key: item.key }),
    });
    if (!response.ok) throw new Error(await responseMessage(response));
    actionResponseSchema.parse(await response.json());
    await refresh();
  }

  async function markAllRead() {
    const response = await fetch(`${apiBaseUrl}/api/v1/notifications/read-all`, {
      method: "POST", credentials: "include", headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error(await responseMessage(response));
    actionResponseSchema.parse(await response.json());
    await refresh();
  }

  async function openItem(item: Notification) {
    if (!item.read) await markRead(item);
    if (item.action === "OPEN_CANCELLATIONS") {
      setOpen(false);
      onOpenCancellations();
    }
  }

  return (
    <div className="notification-center">
      <button
        aria-expanded={open}
        aria-label={`Avisos: ${unreadCount} sin leer`}
        className="notification-bell"
        onClick={() => { setOpen(!open); if (!open) void refresh(); }}
        title="Avisos"
        type="button"
      >
        <svg aria-hidden="true" fill="none" height="22" viewBox="0 0 24 24" width="22"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9ZM10 21h4" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" /></svg>
        {unreadCount > 0 && <strong>{unreadCount > 99 ? "99+" : unreadCount}</strong>}
      </button>
      {open && <section className="notification-popover" aria-label="Avisos de la sucursal">
        <div className="notification-heading">
          <div><h2>Avisos</h2><p>Actividades importantes de la sucursal.</p></div>
          <button className="text-button" disabled={loading || unreadCount === 0} onClick={() => void markAllRead()} type="button">Marcar todo como leído</button>
        </div>
        {message && <p className="form-message error-message">{message}</p>}
        {loading && items.length === 0 && <p className="empty-state">Actualizando avisos…</p>}
        <div className="notification-list">
          {items.map((item) => <article className={item.read ? "notification-item is-read" : "notification-item"} key={item.key}>
            <button className="notification-content" onClick={() => void openItem(item)} type="button">
              <span>{item.title}</span><small>{item.detail}</small>
            </button>
            {!item.read && <button aria-label={`Marcar ${item.title} como leído`} className="notification-read" onClick={() => void markRead(item)} title="Marcar como leído" type="button">✓</button>}
          </article>)}
          {!loading && items.length === 0 && <p className="empty-state">No hay avisos pendientes.</p>}
        </div>
      </section>}
    </div>
  );
}
